import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import type { Subscription } from 'rxjs';
import { buildRequestFor } from '@app/core/build-request';
import { BuildToolService } from '@app/core/services/build-tool.service';
import { canEraseCell } from '@app/core/erase-policy';
import { GameViewportService } from '@app/core/services/game-viewport.service';
import { OnboardingService } from '@app/core/services/onboarding.service';
import { PixiAppFactory } from '@app/core/services/pixi-app-factory.service';
import { SimEngineService } from '@app/core/services/sim-engine.service';
import { TileThumbnailService } from '@app/core/services/tile-thumbnail.service';
import { UiStateService } from '@app/core/services/ui-state.service';
import type { ToolbarDrag } from '@app/core/services/ui-state.service';
import type { PixiGameApp } from '@render/pixi-app';
import { TILE_CREATION_KINDS, TILE_MACHINE_KINDS } from '@render/sprites/tile-registry';
import type { Preview } from '@render/sync/world-renderer';
import { columnOffersMethods } from '@sim/content/market';
import { findBaseEntryConveyor } from '@sim/core/base';
import { findEntityAt } from '@sim/core/grid';
import { feederDirection, inferConveyorDirection, machinePorts } from '@sim/core/routing';
import type { SubscriberColumn } from '@sim/core/subscriber';
import { samePos } from '@sim/core/types';
import type { Direction, GridPos } from '@sim/core/types';

/** Matches how world-renderer.ts aims a tile with no resolved outlet of its own. */
const DEFAULT_GHOST_DIRECTION: Direction = 'east';

/** Identity of a cell in {@link GameCanvasComponent.pipeDragPath}, which needs value equality a `GridPos` object cannot give a `Set`. */
function cellKey(pos: GridPos): string {
  return `${pos.x},${pos.y}`;
}

type SubscriberClickAction =
  { kind: 'place' } | { kind: 'toggle' } | { kind: 'pick'; column: SubscriberColumn; row?: number };

@Component({
  selector: 'app-game-canvas',
  template: `<div
    #host
    class="h-full w-full"
    role="img"
    aria-label="Factory simulation view"
  ></div>`,
})
export class GameCanvasComponent {
  private readonly host = viewChild.required<ElementRef<HTMLDivElement>>('host');
  private readonly engine = inject(SimEngineService);
  private readonly tools = inject(BuildToolService);
  private readonly onboarding = inject(OnboardingService);
  private readonly pixiAppFactory = inject(PixiAppFactory);
  private readonly viewport = inject(GameViewportService);
  private readonly thumbnails = inject(TileThumbnailService);
  private readonly ui = inject(UiStateService);
  private pixiApp?: PixiGameApp;
  private readonly subscriptions: Subscription[] = [];
  private hoveredCell: GridPos | null = null;
  private activeDragDirection: Direction | null = null;
  private layingPipe = false;
  private pipeDragStart: GridPos | null = null;
  private readonly pipeDragPath = new Set<string>();
  private destroyed = false;

  constructor() {
    afterNextRender(() => void this.mount());
    inject(DestroyRef).onDestroy(() => this.teardown());
    effect(() => {
      this.engine.state();
      this.ui.toolbarDrag();
      this.refreshPreview();
    });
    // Read `selectedCell()` unconditionally before the `pixiApp?.` check: gating it behind the
    // optional chain would let the very first run (pixiApp still undefined pre-mount)
    // short-circuit past the signal read entirely, so the effect would never re-fire on change.
    effect(() => {
      const pos = this.tools.selectedCell();
      this.pixiApp?.setSelection(pos);
    });
  }

  private async mount(): Promise<void> {
    const app = await this.pixiAppFactory.create(this.host().nativeElement, this.engine.state());
    if (this.destroyed) {
      app.destroy();
      return;
    }
    this.pixiApp = app;
    this.viewport.register(app);
    app.setInteractionHandlers({
      onHover: (pos) => {
        this.hoveredCell = pos;
        this.ui.hoveredPos.set(pos);
        this.refreshPreview();
      },
      onPaint: (pos, dragDirection, previousCell) => {
        if (dragDirection) this.activeDragDirection = dragDirection;
        this.applyTool(pos, dragDirection, previousCell);
      },
      onPaintEnd: () => {
        this.activeDragDirection = null;
        this.layingPipe = false;
        this.pipeDragStart = null;
        this.pipeDragPath.clear();
        this.refreshPreview();
      },
      onEraseClick: (pos) => {
        if (!this.canEraseAt(pos)) return;
        this.engine.erase(pos);
        this.tools.selectCell(null);
      },
      canStartPipeDrag: (pos) => this.canStartPipeDrag(pos),
      onPipeDragArmed: () => {
        // Closes an open config picker (e.g. `of`'s arg picker) anchored to this cell before the
        // drag has a chance to move underneath it — see `InteractionHandlers.onPipeDragArmed`.
        this.tools.selectCell(null);
      },
      onPipeDragStart: () => {
        this.layingPipe = true;
        this.pipeDragPath.clear();
      },
      onObservableClick: (observableId) => {
        const position = this.engine.state().observables[observableId]?.position;
        if (!position) return;
        this.tools.selectCell(position);
      },
      onSubscriberClick: (pos) => {
        const action = this.subscriberClickAction(pos);
        if (action === null) return;
        if (action.kind === 'place') this.engine.place({ type: 'subscriber' }, pos);
        else if (action.kind === 'toggle') this.engine.toggleSubscribe();
        else this.ui.functionPicker.set({ column: action.column, row: action.row, pos });
      },
      onSubscriberEdgeDrag: (edge, direction) => {
        this.engine.growSubscription(edge, direction);
      },
      canGrowSubscriberEdge: (edge) => this.engine.isEdgeGrowable(edge),
      isSubscriberRegion: (pos) => this.engine.isSubscriberRegion(pos),
      isClickable: (pos) => this.isClickable(pos),
      isEmptyCell: (pos) => !findEntityAt(this.engine.state(), pos),
      isRefused: (pos) => this.engine.isSubscriberSlotFrozen(pos),
      isInteractionAllowed: (pos) => this.onboarding.isMapInteractionAllowed(pos),
    });
    this.subscriptions.push(
      this.engine.events.subscribe((events) => app.applyEvents(this.engine.state(), events)),
      this.engine.layoutChanged.subscribe(() => {
        app.rebuildStatic(this.engine.state());
        app.refreshCursor();
      }),
      this.engine.subscriptionChanged.subscribe(({ subscribed }) =>
        app.setSubscribed(this.engine.state(), subscribed),
      ),
      this.engine.historyRestored.subscribe(() => app.clearPackets()),
      this.ui.resetRequested.subscribe(() => void this.playResetTransition()),
    );
    const tileThumbnails = await app.extractThumbnails();
    // Driven off the tile registry rather than a hand-written list: a kind added to
    // `MACHINE_DEFS`/`CREATION_KINDS` gets its toolbar thumbnail with no second edit here.
    for (const kind of [...TILE_MACHINE_KINDS, ...TILE_CREATION_KINDS, 'subject' as const]) {
      this.thumbnails.set(kind, tileThumbnails[kind]);
    }
  }

  private applyTool(
    pos: GridPos,
    dragDirection: Direction | null,
    previousCell: GridPos | null,
  ): void {
    if (this.layingPipe) {
      if (previousCell && this.pipeDragStart === null) {
        this.pipeDragStart = previousCell;
      }
      const key = cellKey(pos);
      const backtracking =
        this.pipeDragPath.has(key) ||
        (this.pipeDragStart !== null && samePos(pos, this.pipeDragStart));
      if (backtracking && previousCell) {
        const prevKey = cellKey(previousCell);
        if (this.pipeDragPath.has(prevKey)) {
          this.engine.erase(previousCell);
          this.pipeDragPath.delete(prevKey);
        }
      } else if (!backtracking) {
        if (dragDirection && previousCell)
          this.engine.redirectConveyor(previousCell, dragDirection);
        const direction = dragDirection ?? inferConveyorDirection(this.engine.state(), pos);
        this.engine.place({ type: 'conveyor', direction }, pos);
        this.pipeDragPath.add(key);
      }
      return;
    }
    const selected = this.canEraseAt(pos) ? pos : null;
    this.tools.selectCell(selected);
    this.pixiApp?.setSelection(selected);
  }

  private isClickable(pos: GridPos): boolean {
    if (this.engine.isSubscriberRegion(pos)) return this.subscriberClickAction(pos) !== null;
    return this.canEraseAt(pos);
  }

  /**
   * The single answer to "what does a click on this Subscriber cell do", shared by the click handler
   * and the hover cursor so the pointer can never promise an interaction that won't happen. A live
   * subscription offers nothing but its plug — its functions are frozen until it is torn down.
   */
  private subscriberClickAction(pos: GridPos): SubscriberClickAction | null {
    const state = this.engine.state();
    if (state.base === null) return { kind: 'place' };
    const entry = findBaseEntryConveyor(state);
    if (entry && samePos(entry.position, pos)) {
      return this.onboarding.canSubscribe() ? { kind: 'toggle' } : null;
    }
    if (!this.engine.canEditSubscription()) return null;
    const slot = this.engine.subscriberSlotInfo(pos);
    if (slot && !columnOffersMethods(slot.column)) return null;
    if (slot?.state === 'next-empty') return { kind: 'pick', column: slot.column };
    if (slot?.state === 'filled') return { kind: 'pick', column: slot.column, row: slot.row };
    return null;
  }

  private canStartPipeDrag(pos: GridPos): boolean {
    // The entry conveyor plugged into the Subscriber is where a plain click toggles the
    // subscription — it must never be claimed as a drag-start cell, or that click can never land.
    if (this.engine.isSubscriberRegion(pos)) return false;
    const kind = findEntityAt(this.engine.state(), pos)?.kind;
    return kind === 'observable' || kind === 'conveyor' || kind === 'machine';
  }

  /** Dissolves the current layout away, resets underneath it, then brings the fresh grid back in. */
  private async playResetTransition(): Promise<void> {
    if (!this.pixiApp) return;
    await this.pixiApp.playDissolveOut();
    this.engine.resetGame();
    this.onboarding.reset();
    this.pixiApp.resetView();
    await this.pixiApp.playDissolveIn();
  }

  private canEraseAt(pos: GridPos): boolean {
    return canEraseCell(this.engine, this.onboarding, pos);
  }

  private refreshPreview(): void {
    if (!this.pixiApp) return;
    this.pixiApp.setPreview(this.buildPreview());
  }

  /**
   * The ghost riding a toolbar drag, painted on the cell the drop would actually land on — disabled
   * wherever that tool cannot go, so carrying an observable across the pipe stream shows a refusal
   * before the drop rather than nothing at all.
   */
  private toolbarDragPreview(drag: ToolbarDrag): Preview | null {
    const pos = this.viewport.cellAtClientPoint(drag.x, drag.y);
    if (!pos) return null;
    return {
      kind: 'build',
      pos,
      tool: drag.kind,
      direction: this.ghostDirection(pos),
      valid: this.engine.canPlace(buildRequestFor(drag.kind), pos),
    };
  }

  /** How the ghost is turned: an operator inherits the pipe it would slot onto, anything else faces the way its own tile would once placed. */
  private ghostDirection(pos: GridPos): Direction {
    const state = this.engine.state();
    const occupant = findEntityAt(state, pos);
    if (occupant?.kind === 'conveyor') return state.conveyors[occupant.id].direction;
    return machinePorts(state, pos).outSide ?? DEFAULT_GHOST_DIRECTION;
  }

  private buildPreview(): Preview | null {
    const drag = this.ui.toolbarDrag();
    if (drag) return this.toolbarDragPreview(drag);
    const pos = this.hoveredCell;
    if (!pos) return null;
    if (this.layingPipe) {
      if (this.pipeDragPath.has(cellKey(pos))) {
        return { kind: 'erase', pos, valid: true };
      }
      const state = this.engine.state();
      const feeder = this.activeDragDirection ?? feederDirection(state, pos);
      const direction = feeder ?? inferConveyorDirection(state, pos);
      return {
        kind: 'build',
        pos,
        tool: 'conveyor',
        direction,
        omnidirectional: feeder === null,
        valid: this.engine.canPlace({ type: 'conveyor', direction }, pos),
      };
    }
    return { kind: 'hover', pos };
  }

  private teardown(): void {
    this.destroyed = true;
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.viewport.clear();
    this.pixiApp?.destroy();
  }
}
